<template>
  <div id="reviews-section" class="product-reviews-section mt-12 pt-8 border-t border-slate-200 dark:border-slate-800">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
      <div>
        <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 text-xs font-black uppercase tracking-wider mb-2">
          <span>⭐ Customer Feedback</span>
        </div>
        <h2 class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
          Ratings & Reviews
        </h2>
        <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
          Honest feedback exclusively from verified buyers who purchased this item.
        </p>
      </div>

      <button
        v-if="canRate && !showReviewForm"
        @click="openReviewForm"
        class="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 transition-all active:scale-95 shrink-0"
      >
        <span>✍️</span>
        <span>{{ userRating ? 'Edit Your Review' : 'Write a Review' }}</span>
      </button>
    </div>

    <!-- Rating Summary Dashboard Card -->
    <div class="bg-white dark:bg-slate-900/90 rounded-3xl border border-slate-200/80 dark:border-slate-800 p-6 sm:p-8 shadow-sm backdrop-blur-xl mb-8">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <!-- Big Score Column (4 cols) -->
        <div class="lg:col-span-4 flex flex-col items-center justify-center text-center p-4 border-b lg:border-b-0 lg:border-r border-slate-100 dark:border-slate-800">
          <div class="text-5xl sm:text-6xl font-black text-slate-900 dark:text-white tracking-tighter mb-2">
            {{ formatScore(averageRating) }}
          </div>
          <div class="mb-2">
            <StarRating :rating="Number(averageRating)" size="md" />
          </div>
          <span class="text-xs font-bold text-slate-500 dark:text-slate-400">
            Based on <strong class="text-slate-800 dark:text-slate-200">{{ totalCount }}</strong> verified buyer {{ totalCount === 1 ? 'review' : 'reviews' }}
          </span>
        </div>

        <!-- Breakdown Bars Column (8 cols) -->
        <div class="lg:col-span-8 flex flex-col gap-2.5">
          <div
            v-for="star in [5, 4, 3, 2, 1]"
            :key="star"
            class="flex items-center gap-3 text-xs font-bold"
          >
            <span class="w-8 text-slate-600 dark:text-slate-300 flex items-center justify-end gap-1">
              <span>{{ star }}</span>
              <span class="text-amber-400 text-xs">★</span>
            </span>

            <!-- Progress Bar -->
            <div class="flex-1 h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden relative">
              <div
                class="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-amber-400 to-orange-400"
                :style="{ width: getPercentage(breakdown[star]) + '%' }"
              ></div>
            </div>

            <!-- Count & Percentage -->
            <div class="w-16 text-right text-slate-400 font-mono text-[11px]">
              {{ breakdown[star] || 0 }} ({{ getPercentage(breakdown[star]) }}%)
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Review Submission Form (Expandable or Inline) -->
    <div
      v-if="showReviewForm"
      class="bg-gradient-to-br from-amber-500/5 via-slate-50 to-white dark:from-amber-500/10 dark:via-slate-900 dark:to-slate-900 rounded-3xl border-2 border-amber-500/30 p-6 sm:p-8 shadow-xl mb-8 relative transition-all"
    >
      <div class="flex items-center justify-between mb-5">
        <div class="flex items-center gap-2">
          <span class="text-2xl">✍️</span>
          <div>
            <h3 class="text-lg font-black text-slate-900 dark:text-white">
              {{ userRating ? 'Update Your Verified Review' : 'Drop a Rating & Review' }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              Verified Purchase &bull; You completed an order for this item
            </p>
          </div>
        </div>
        <button
          @click="showReviewForm = false"
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm font-black p-1.5 rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-800 transition-colors"
          title="Close Form"
        >
          ✕
        </button>
      </div>

      <!-- Rating Star Selector -->
      <div class="mb-5">
        <label class="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
          Your Rating <span class="text-rose-500">*</span>
        </label>
        <div class="flex items-center gap-4">
          <StarRating
            v-model="form.rating"
            :interactive="true"
            size="lg"
          />
          <span class="text-sm font-extrabold text-amber-500">
            {{ ratingLabels[form.rating] || 'Select stars' }}
          </span>
        </div>
      </div>

      <!-- Review Comment Textarea -->
      <div class="mb-4">
        <label class="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
          Your Review (Optional)
        </label>
        <textarea
          v-model="form.comment"
          rows="3"
          maxlength="1000"
          placeholder="Share your experience with this product... Quality, delivery, features, or tips for others."
          class="w-full px-4 py-3 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all placeholder:text-slate-400"
        ></textarea>
        <div class="flex justify-between items-center mt-1 text-[11px] text-slate-400">
          <span>Be constructive and respect community guidelines.</span>
          <span>{{ form.comment.length }} / 1000</span>
        </div>
      </div>

      <!-- Anonymous Review Toggle -->
      <div class="mb-5 flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/60">
        <div class="flex items-center gap-3">
          <span class="text-xl">🎭</span>
          <div>
            <div class="text-xs font-bold text-slate-800 dark:text-slate-200">
              Post review anonymously
            </div>
            <div class="text-[11px] text-slate-500 dark:text-slate-400">
              Your name and profile will be hidden from other shoppers (still shown as verified buyer).
            </div>
          </div>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            v-model="form.is_anonymous"
            class="sr-only peer"
          />
          <div class="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-amber-500"></div>
        </label>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div class="flex items-center gap-2">
          <button
            @click="submitReview"
            :disabled="submitting || form.rating < 1"
            class="px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            <span v-if="submitting" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <span>{{ userRating ? 'Update Review' : 'Publish Review' }}</span>
          </button>

          <button
            @click="showReviewForm = false"
            class="px-4 py-2.5 rounded-xl font-bold text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            Cancel
          </button>
        </div>

        <button
          v-if="userRating"
          @click="deleteUserReview"
          :disabled="deleting"
          class="text-xs font-bold text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 hover:underline flex items-center gap-1"
        >
          <span v-if="deleting" class="w-3 h-3 border-2 border-rose-500 border-t-transparent rounded-full animate-spin"></span>
          <span>Delete My Review</span>
        </button>
      </div>

      <!-- Error / Success Notice -->
      <div v-if="feedbackMessage" :class="['mt-4 p-3 rounded-xl text-xs font-bold flex items-center gap-2', feedbackSuccess ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20']">
        <span>{{ feedbackSuccess ? '✓' : '⚠️' }}</span>
        <span>{{ feedbackMessage }}</span>
      </div>
    </div>


    <!-- Reviews List Section -->
    <div class="space-y-4">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
          <span>Customer Reviews</span>
          <span class="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
            {{ totalCount }}
          </span>
        </h3>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="space-y-3">
        <div v-for="i in 3" :key="i" class="h-28 rounded-2xl bg-white dark:bg-slate-900/70 border border-slate-100 dark:border-slate-800 animate-pulse"></div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="reviews.length === 0"
        class="bg-white dark:bg-slate-900/80 rounded-3xl border border-slate-200/80 dark:border-slate-800 p-8 sm:p-12 text-center"
      >
        <div class="w-16 h-16 rounded-full bg-amber-500/10 text-amber-500 text-3xl flex items-center justify-center mx-auto mb-4">
          ★
        </div>
        <h4 class="text-base font-black text-slate-900 dark:text-white mb-1">
          No Reviews Yet
        </h4>
        <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
          Be the first verified buyer to share your feedback and help the community!
        </p>
      </div>

      <!-- Review Cards Grid -->
      <div v-else class="space-y-3.5">
        <article
          v-for="review in reviews"
          :key="review.id"
          class="bg-white dark:bg-slate-900/90 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow relative"
        >
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <!-- User Info & Avatar -->
            <div class="flex items-center gap-3">
              <div
                v-if="review.is_anonymous && !review.is_owner"
                class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-black text-base flex items-center justify-center shadow-sm shrink-0 border border-slate-200 dark:border-slate-700"
              >
                🎭
              </div>
              <div
                v-else-if="review.is_anonymous && review.is_owner"
                class="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-black text-base flex items-center justify-center shadow-sm shrink-0"
              >
                🎭
              </div>
              <div
                v-else
                class="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white font-black text-sm flex items-center justify-center shadow-sm shrink-0"
              >
                {{ getInitials(review.user?.name) }}
              </div>

              <div>
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white">
                    <template v-if="review.is_anonymous && !review.is_owner">
                      Anonymous Buyer
                    </template>
                    <template v-else-if="review.is_anonymous && review.is_owner">
                      {{ review.user?.name || 'You' }} <span class="text-xs font-normal text-slate-400">(Anonymous to public)</span>
                    </template>
                    <template v-else>
                      {{ review.user?.name || 'Verified Customer' }}
                    </template>
                  </span>
                  <!-- Verified Buyer Badge -->
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black border border-emerald-500/20">
                    <span>✓</span> Verified Buyer
                  </span>
                  <!-- Anonymous Badge -->
                  <span
                    v-if="review.is_anonymous"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-bold border border-slate-200 dark:border-slate-700"
                  >
                    <span>🎭</span> Anonymous
                  </span>
                </div>
                <div class="text-[11px] text-slate-400 font-medium mt-0.5">
                  {{ formatDate(review.created_at) }}
                </div>
              </div>
            </div>

            <!-- Stars -->
            <div class="flex items-center gap-1 sm:self-start">
              <StarRating :rating="review.rating" size="sm" />
            </div>
          </div>

          <!-- Review Comment -->
          <p
            v-if="review.comment"
            class="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed pl-13 sm:pl-0 mt-2 whitespace-pre-line"
          >
            {{ review.comment }}
          </p>
        </article>
      </div>

      <!-- Pagination / Load More -->
      <div v-if="pagination.last_page > 1" class="flex justify-center items-center gap-3 pt-4">
        <button
          v-if="pagination.current_page > 1"
          @click="fetchPage(pagination.current_page - 1)"
          class="px-3.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          &larr; Previous
        </button>
        <span class="text-xs text-slate-400 font-mono">
          Page {{ pagination.current_page }} of {{ pagination.last_page }}
        </span>
        <button
          v-if="pagination.current_page < pagination.last_page"
          @click="fetchPage(pagination.current_page + 1)"
          class="px-3.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          Next &rarr;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth.js';
import { ratingService } from '@/services/api.js';
import StarRating from '@/components/ui/StarRating.vue';

export default {
  name: 'ProductReviewsSection',
  components: {
    StarRating
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (v) => ['subcategory', 'physical'].includes(v)
    },
    targetId: {
      type: [Number, String],
      required: true
    },
    productTitle: {
      type: String,
      default: ''
    }
  },
  emits: ['rating-updated'],
  setup(props, { emit }) {
    const route = useRoute();
    const authStore = useAuthStore();

    const loading = ref(false);
    const submitting = ref(false);
    const deleting = ref(false);
    const showReviewForm = ref(false);

    const averageRating = ref(0);
    const totalCount = ref(0);
    const breakdown = ref({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
    const reviews = ref([]);
    const canRate = ref(false);
    const userRating = ref(null);

    const pagination = reactive({
      current_page: 1,
      last_page: 1
    });

    const form = reactive({
      rating: 5,
      comment: '',
      is_anonymous: false
    });

    const feedbackMessage = ref('');
    const feedbackSuccess = ref(true);

    const ratingLabels = {
      1: 'Poor (1/5)',
      2: 'Fair (2/5)',
      3: 'Good (3/5)',
      4: 'Very Good (4/5)',
      5: 'Excellent! (5/5)'
    };

    const fetchRatings = async (page = 1) => {
      if (!props.targetId) return;
      loading.value = true;
      try {
        const res = await ratingService.getRatings(props.type, props.targetId, { page, per_page: 10 });
        if (res.success) {
          averageRating.value = res.average_rating || 0;
          totalCount.value = res.total_count || 0;
          breakdown.value = res.breakdown || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
          reviews.value = res.ratings?.data || [];
          pagination.current_page = res.ratings?.current_page || 1;
          pagination.last_page = res.ratings?.last_page || 1;
          canRate.value = res.can_rate || false;
          userRating.value = res.user_rating || null;

          if (userRating.value) {
            form.rating = userRating.value.rating;
            form.comment = userRating.value.comment || '';
            form.is_anonymous = !!userRating.value.is_anonymous;
          }

          emit('rating-updated', {
            average_rating: averageRating.value,
            ratings_count: totalCount.value
          });
        }
      } catch (err) {
        console.error('Failed to load ratings:', err);
      } finally {
        loading.value = false;
      }
    };

    const fetchPage = (p) => {
      fetchRatings(p);
    };

    const openReviewForm = () => {
      if (userRating.value) {
        form.rating = userRating.value.rating;
        form.comment = userRating.value.comment || '';
        form.is_anonymous = !!userRating.value.is_anonymous;
      } else {
        form.rating = 5;
        form.comment = '';
        form.is_anonymous = false;
      }
      feedbackMessage.value = '';
      showReviewForm.value = true;
    };

    const submitReview = async () => {
      if (form.rating < 1 || form.rating > 5) return;
      submitting.value = true;
      feedbackMessage.value = '';

      try {
        const res = await ratingService.submitRating(props.type, props.targetId, {
          rating: form.rating,
          comment: form.comment,
          is_anonymous: form.is_anonymous
        });

        if (res.success) {
          feedbackSuccess.value = true;
          feedbackMessage.value = res.message || 'Rating submitted successfully!';
          userRating.value = res.rating;
          await fetchRatings(pagination.current_page);
          setTimeout(() => {
            showReviewForm.value = false;
            feedbackMessage.value = '';
          }, 1500);
        } else {
          feedbackSuccess.value = false;
          feedbackMessage.value = res.message || 'Failed to submit rating.';
        }
      } catch (err) {
        feedbackSuccess.value = false;
        feedbackMessage.value = err.response?.data?.message || 'Error submitting review. Please try again.';
      } finally {
        submitting.value = false;
      }
    };

    const deleteUserReview = async () => {
      if (!userRating.value?.id) return;
      if (!confirm('Are you sure you want to remove your review?')) return;

      deleting.value = true;
      try {
        const res = await ratingService.deleteRating(userRating.value.id);
        if (res.success) {
          userRating.value = null;
          form.rating = 5;
          form.comment = '';
          form.is_anonymous = false;
          showReviewForm.value = false;
          await fetchRatings(1);
        }
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to delete review.');
      } finally {
        deleting.value = false;
      }
    };

    const formatScore = (score) => {
      return Number(score || 0).toFixed(1);
    };

    const getPercentage = (count) => {
      if (!totalCount.value || !count) return 0;
      return Math.round((count / totalCount.value) * 100);
    };

    const getInitials = (name) => {
      if (!name) return 'U';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateStr;
      }
    };

    const checkHashAndScroll = () => {
      if (route && (route.hash === '#reviews-section' || route.query?.rate)) {
        setTimeout(() => {
          const el = document.getElementById('reviews-section');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
          if (canRate.value && !showReviewForm.value) {
            openReviewForm();
          }
        }, 500);
      }
    };

    watch(() => props.targetId, (newId) => {
      if (newId) {
        fetchRatings(1);
      }
    });

    watch(() => route.hash, () => {
      checkHashAndScroll();
    });

    onMounted(async () => {
      await fetchRatings(1);
      checkHashAndScroll();
    });

    return {
      authStore,
      loading,
      submitting,
      deleting,
      showReviewForm,
      averageRating,
      totalCount,
      breakdown,
      reviews,
      canRate,
      userRating,
      pagination,
      form,
      feedbackMessage,
      feedbackSuccess,
      ratingLabels,
      fetchRatings,
      fetchPage,
      openReviewForm,
      submitReview,
      deleteUserReview,
      formatScore,
      getPercentage,
      getInitials,
      formatDate
    };
  }
};
</script>

<style scoped>
.scrollbar-none::-webkit-scrollbar {
  display: none;
}
</style>
